import React, { useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import styled, { useTheme } from 'styled-components';

import { useTenant } from '../../../../app/context/tenant-context';
import {
  useGetUserCollections,
  useGetUserSharedCollections,
  useGetUserSharedViewsV2,
  useGetUserViewsV2,
} from '../../../../app/hooks/accounts';
import { getAppRouteParams } from '../../../../app/utils/app';
import { useAppContext } from '../../../context/app-context';
import { useTabManager } from '../../../context/tab-manager-context';
import { useSwitchTenants, useTenants } from '../../../hooks/tenants';

import { Dropdown, Icon, Tooltip } from '@controlrooms/components';
import { ICONS } from '@controlrooms/constants';
import { PersistCollection, PersistView } from '@controlrooms/models';

const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  flex: 1;
  padding: 32px 24px 48px;
  text-align: center;
  background-color: ${({ theme }) => theme.v2.tab.emptyStateBackgroundColor};
  overflow-y: auto;
`;

const TopSection = styled.div`
  width: 100%;
  max-width: 560px;
  margin-bottom: 32px;
`;

const LogoImage = styled.img`
  display: block;
  height: 48px;
  width: auto;
  margin-bottom: 16px;
  opacity: 0.9;
`;

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${({ theme }) => theme.v2.tab.emptyStateTitleColor};
  margin: 0 0 8px 0;
  font-family: 'Open Sans', sans-serif;
`;

const Description = styled.p`
  font-size: 16px;
  color: ${({ theme }) => theme.v2.tab.emptyStateTextColor};
  margin: 0;
  max-width: 500px;
  line-height: 1.5;
  font-family: 'Open Sans', sans-serif;
`;

const TenantSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-top: 24px;
  width: 100%;
`;

const TenantLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${({ theme }) => theme.v2.tab.emptyStateTextColor};
  opacity: 0.8;
  font-family: 'Open Sans', sans-serif;
`;

const TenantSelectorWrapper = styled.div`
  width: 100%;
  max-width: 280px;
  border-radius: 8px;
  overflow: visible;
`;

const TenantDropdownTrigger = styled(Dropdown.Trigger)`
  width: 100%;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.v2.tab.borderColor};
  background-color: ${({ theme }) => theme.v2.tab.backgroundColor};
  color: ${({ theme }) => theme.v2.tab.textColor};
  font-size: 14px;
  font-family: 'Open Sans', sans-serif;
  text-align: left;

  &:hover {
    background-color: ${({ theme }) => theme.v2.tab.hoverBackgroundColor};
  }

  svg {
    flex-shrink: 0;
    path {
      fill: ${({ theme }) => theme.v2.tab.textColor};
    }
  }
`;

const TenantTriggerLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const TenantDropdownOptions = styled(Dropdown.Options)`
  width: 100%;
  min-width: 280px;
  max-height: 280px;
  overflow-y: auto;
`;

const QuickStartSection = styled.div`
  width: 100%;
  max-width: 900px;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, minmax(160px, 1fr));
  gap: 20px;
  margin-top: 8px;
  flex: 1;
  min-height: 0;
  min-height: 360px;
`;

const GridCell = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 400px;
  overflow-x: hidden;
  background-color: ${({ theme }) => theme.v2.tab.backgroundColor};
  border: 1px solid ${({ theme }) => theme.v2.tab.borderColor};
  border-radius: 8px;
  padding: 12px;
`;

const GridCellCenterWrap = styled.div`
  grid-column: 1 / -1;
  display: flex;
  justify-content: center;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.v2.tab.emptyStateTitleColor};
  margin: 0 0 8px 0;
  border-bottom: 1px solid ${({ theme }) => theme.v2.tab.borderColor};
  padding-bottom: 8px;
  text-align: center;
  font-family: 'Open Sans', sans-serif;
  flex-shrink: 0;
`;

const ItemList = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 8px;
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
  flex: 1;
  width: 100%;

  & > * {
    flex: 0 0 auto;
  }
`;

const CHIP_MAX_WIDTH = 120;

const QuickStartChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: ${CHIP_MAX_WIDTH}px;
  min-width: 0;
  flex: 0 1 auto;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.v2.tab.borderColor};
  background-color: ${({ theme }) => theme.v2.tab.backgroundColor};
  color: ${({ theme }) => theme.v2.tab.textColor};
  font-size: 14px;
  font-family: 'Open Sans', sans-serif;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  overflow: hidden;

  &:hover {
    background-color: ${({ theme }) => theme.v2.tab.hoverBackgroundColor};
    border-color: ${({ theme }) => theme.v2.tab.activeBorderColor};
  }

  &:focus {
    outline: none;
  }
`;

const ChipLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ChipIcon = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ArrowHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: ${({ theme }) => theme.v2.tab.emptyStateTextColor};
  font-size: 14px;
  opacity: 0.7;
  font-family: 'Open Sans', sans-serif;
`;

const ArrowIconWrapper = styled.div`
  transform: rotate(180deg);
`;

const EmptyStateTenantSelector: React.FC = () => {
  const { tenants } = useTenants();
  const { tenant: currentTenantId } = useTenant();
  const { switchTenant } = useSwitchTenants();
  const { isTenantSwitching } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  const queryClient = useQueryClient();
  const location = useLocation();

  const tenantIdFromUrl = getAppRouteParams(location.pathname)?.params?.tenantId || '0';
  const currentTenantName =
    tenants?.find((t) => t.id === parseInt(tenantIdFromUrl, 10))?.name ?? '';

  const handleTenantChange = async (tenantId: number) => {
    const hasAccess = tenants?.some((t) => t.id === tenantId);
    setIsOpen(false);
    if (hasAccess) {
      await switchTenant(tenantId);
      queryClient.clear();
    }
  };

  return (
    <Dropdown.Root isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <TenantDropdownTrigger
        onClick={() => !isTenantSwitching && setIsOpen((prev) => !prev)}
        as="button"
        type="button"
        disabled={isTenantSwitching}
      >
        <TenantTriggerLabel>{currentTenantName}</TenantTriggerLabel>
        {tenants?.length ? (
          <Icon name={ICONS.Chevron} width="8" height="8" style={{ marginLeft: '10px' }} />
        ) : null}
      </TenantDropdownTrigger>
      <TenantDropdownOptions open={isOpen} position="center" vertical="bottom" width="100%">
        {tenants?.map((t) => (
          <Dropdown.Item
            key={t.id}
            onClick={() => handleTenantChange(t.id)}
            active={t.id === currentTenantId}
          >
            {t.name}
          </Dropdown.Item>
        ))}
      </TenantDropdownOptions>
    </Dropdown.Root>
  );
};

export const EmptyState: React.FC = () => {
  const { data: userSharedViews } = useGetUserSharedViewsV2();
  const { data: userSharedCollections } = useGetUserSharedCollections();
  const { data: _userViews } = useGetUserViewsV2();
  const { data: userCollections } = useGetUserCollections();

  const userViews = useMemo(
    () => _userViews?.filter((v) => v.default_view === false) || [],
    [_userViews],
  );

  const sharedList = useMemo(() => {
    return [
      ...(userSharedViews || []).map((v) => ({
        name: v.view.name,
        id: v.view_id || '',
        isCollection: false,
        entity: v,
      })),
      ...(userSharedCollections || []).map((c) => ({
        name: c.collection_name,
        id: c.collection_id || '',
        isCollection: true,
        entity: c,
      })),
    ];
  }, [userSharedCollections, userSharedViews]);

  const { openSavedView, loadCollection, closeCollection } = useTabManager();

  const hasQuickStart =
    userViews.length > 0 || (userCollections?.length ?? 0) > 0 || sharedList.length > 0;

  const onlyViews = userViews.length > 0 && (userCollections?.length ?? 0) === 0;
  const onlyCollections = (userCollections?.length ?? 0) > 0 && userViews.length === 0;

  const handleLoadView = async (view: PersistView) => {
    if (!view.view_id) return;
    closeCollection();
    await openSavedView(view.view_id);
  };

  const handleLoadCollection = async (collection: PersistCollection) => {
    if (collection.views?.length) {
      await loadCollection(collection);
    }
  };

  const theme = useTheme();

  return (
    <EmptyStateContainer data-testid="empty-state-no-tabs">
      <TopSection>
        <IconContainer>
          <LogoImage src={theme.v2.tab.emptyStateLogo} alt="ControlRooms.ai" />
        </IconContainer>
        <Title>Get Started</Title>
        <Description>
          {hasQuickStart
            ? 'Click on any menu item on the left to open a new tab, or pick a saved view below to start quickly.'
            : 'Click on any menu item on the left to open a new tab.'}
        </Description>

        <TenantSection>
          <TenantLabel>Tenant</TenantLabel>
          <TenantSelectorWrapper>
            <EmptyStateTenantSelector />
          </TenantSelectorWrapper>
        </TenantSection>
      </TopSection>

      {hasQuickStart && (
        <QuickStartSection>
          {userViews.length > 0 &&
            (onlyViews ? (
              <GridCellCenterWrap>
                <GridCell>
                  <SectionTitle>Your saved tabs</SectionTitle>
                  <ItemList>
                    {userViews.map((view) => (
                      <Tooltip
                        key={view.view_id}
                        version="v2"
                        delayShow={1000}
                        renderInPortal
                        label={view?.view?.name || 'Unnamed view'}
                      >
                        <QuickStartChip onClick={() => handleLoadView(view)}>
                          <ChipLabel>{view?.view?.name || 'Unnamed view'}</ChipLabel>
                        </QuickStartChip>
                      </Tooltip>
                    ))}
                  </ItemList>
                </GridCell>
              </GridCellCenterWrap>
            ) : (
              <GridCell>
                <SectionTitle>Your saved tabs</SectionTitle>
                <ItemList>
                  {userViews.map((view) => (
                    <Tooltip
                      key={view.view_id}
                      version="v2"
                      delayShow={1000}
                      renderInPortal
                      label={view?.view?.name || 'Unnamed view'}
                    >
                      <QuickStartChip onClick={() => handleLoadView(view)}>
                        <ChipLabel>{view?.view?.name || 'Unnamed view'}</ChipLabel>
                      </QuickStartChip>
                    </Tooltip>
                  ))}
                </ItemList>
              </GridCell>
            ))}

          {userCollections &&
            userCollections.length > 0 &&
            (onlyCollections ? (
              <GridCellCenterWrap>
                <GridCell>
                  <SectionTitle>Your collections</SectionTitle>
                  <ItemList>
                    {userCollections.map((collection) => (
                      <Tooltip
                        key={collection.collection_id ?? collection.collection_name}
                        version="v2"
                        delayShow={1000}
                        renderInPortal
                        label={collection.collection_name || 'Unnamed collection'}
                      >
                        <QuickStartChip onClick={() => handleLoadCollection(collection)}>
                          <ChipIcon>
                            <Icon name={ICONS.Folder} width="16" height="16" />
                          </ChipIcon>
                          <ChipLabel>
                            {collection.collection_name || 'Unnamed collection'}
                          </ChipLabel>
                        </QuickStartChip>
                      </Tooltip>
                    ))}
                  </ItemList>
                </GridCell>
              </GridCellCenterWrap>
            ) : (
              <GridCell>
                <SectionTitle>Your collections</SectionTitle>
                <ItemList>
                  {userCollections.map((collection) => (
                    <Tooltip
                      key={collection.collection_id ?? collection.collection_name}
                      version="v2"
                      delayShow={1000}
                      renderInPortal
                      label={collection.collection_name || 'Unnamed collection'}
                    >
                      <QuickStartChip onClick={() => handleLoadCollection(collection)}>
                        <ChipIcon>
                          <Icon name={ICONS.Folder} width="16" height="16" />
                        </ChipIcon>
                        <ChipLabel>{collection.collection_name || 'Unnamed collection'}</ChipLabel>
                      </QuickStartChip>
                    </Tooltip>
                  ))}
                </ItemList>
              </GridCell>
            ))}

          {sharedList.length > 0 && (
            <GridCellCenterWrap>
              <GridCell>
                <SectionTitle>Shared with you</SectionTitle>
                <ItemList>
                  {sharedList.map((item) => (
                    <Tooltip
                      key={item.id}
                      version="v2"
                      delayShow={1000}
                      renderInPortal
                      label={item.name}
                    >
                      <QuickStartChip
                        onClick={() =>
                          item.isCollection
                            ? handleLoadCollection(item.entity as PersistCollection)
                            : handleLoadView(item.entity as PersistView)
                        }
                      >
                        {item.isCollection && (
                          <ChipIcon>
                            <Icon name={ICONS.Folder} width="16" height="16" />
                          </ChipIcon>
                        )}
                        <ChipLabel>{item.name}</ChipLabel>
                      </QuickStartChip>
                    </Tooltip>
                  ))}
                </ItemList>
              </GridCell>
            </GridCellCenterWrap>
          )}
        </QuickStartSection>
      )}

      {!hasQuickStart && (
        <ArrowHint style={{ marginTop: 32 }}>
          <ArrowIconWrapper>
            <Icon name={ICONS.ArrowRight} width="20" height="20" />
          </ArrowIconWrapper>
          <span>Start from the left menu</span>
        </ArrowHint>
      )}
    </EmptyStateContainer>
  );
};
